import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import useApp from "../hooks/useApp";
import useChatBotKey from "./useChatBotKey";
import { buildMercuryContext, MercuryContextUpdate, toContextUpdates, widgetElementId } from "./mercuryContext";
import { fetchEGate } from "../helpers/eGateApi";
import "./chatBot.css";

const SCRIPT_ID = "mercury-widget-snippet";
const ORIGIN = "https://rtc.mercury.ai/";
// Comfortably under the backend's CookieExpirationMinutes (15, see appsettings.json)
const HEARTBEAT_INTERVAL = 5 * 60 * 1000;

interface MercuryLocalConfig {
  context?: Record<string, string>;
}

declare global {
  interface Window {
    mercuryConfig?: Record<string, MercuryLocalConfig>;
    mercuryWidget?: Record<string, unknown>;
  }
}

/**
 * The element the widget mounts into. Mercury attaches its API to it once the widget is ready
 * (announced by a "widget-ready" event) and dispatches "opened"/"closed" on it.
 */
interface MercuryContainer extends HTMLElement {
  __vue_app__?: { unmount?: () => void };
  updateContext?: (updates: MercuryContextUpdate[]) => Promise<unknown>;
  setLanguage?: (language: string) => Promise<unknown>;
}

/**
 * The channel's widget.js carries its own config, so it is all there is to load. It mounts into
 * the element with the channel's id, creating one if missing; creating it here lets us listen to it
 * before the widget is up. The widget reads `window.mercuryConfig[key]` once, when it starts.
 */
function injectWidget(key: string, config: MercuryLocalConfig) {
  const container = document.createElement("div");
  container.id = widgetElementId(key);
  document.body.append(container);

  window.mercuryConfig = { ...window.mercuryConfig, [key]: config };

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = `${ORIGIN}delivery/${encodeURIComponent(key)}/widget.js`;
  script.dataset.key = key;
  document.body.append(script);
}

/**
 * Mercury exposes no teardown. The widget is a Vue 3 app though, and Vue parks the app handle on
 * the element it mounts into, so unmounting through that takes the widget down — including its SSE
 * stream to rtc.mercury.ai — without the page reload this used to need. Undocumented, hence the
 * caller's fallback when the handle is missing.
 *
 * Everything Mercury injected goes with it, so the next sign-in re-injects from scratch: widget.js
 * starts the widget only when it is evaluated.
 */
function unloadWidget(key: string) {
  const container = document.getElementById(widgetElementId(key)) as MercuryContainer | null;
  const app = container?.__vue_app__;
  if (typeof app?.unmount !== "function") return false;

  try {
    app.unmount();
  } catch {
    return false;
  }

  container?.remove();
  document.getElementById(SCRIPT_ID)?.remove();
  document.querySelectorAll('style[id^="mercury-widget-style"]').forEach((el) => el.remove());
  delete window.mercuryConfig?.[key];
  delete window.mercuryWidget?.[key];
  return true;
}

export default function ChatBot() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const { appData } = useApp();
  const { key, isPending, features } = useChatBotKey();

  const language = i18n.language;
  const context = buildMercuryContext(pathname, appData?.auth?.roles ?? [], features);
  const contextJson = JSON.stringify(context);

  // What the widget was last told, so it is only updated on actual changes: every update makes it
  // open a conversation with Mercury, which should wait until the user opens the chat
  const sent = useRef<{ language?: string; contextJson?: string }>({});

  useEffect(() => {
    if (!key) return;
    const config = window.mercuryConfig?.[key];
    if (config) config.context = { ...context, "setting.language": language };

    const widget = document.getElementById(widgetElementId(key)) as MercuryContainer | null;
    if (typeof widget?.updateContext !== "function") return;

    // The chat bot must never surface errors to the user. Mercury rejects context parameters missing
    // from the channel's whitelist, so the language goes on its own to not be rejected with them.
    try {
      if (sent.current.language !== language) {
        void widget.setLanguage?.(language).catch(() => undefined);
      }
      if (sent.current.contextJson !== contextJson) {
        void widget.updateContext(toContextUpdates(context)).catch(() => undefined);
      }
    } catch {
      // see above
    }
    sent.current = { language, contextJson };
  }, [key, contextJson, language]);

  useEffect(() => {
    if (isPending) return;
    const injectedKey = document.getElementById(SCRIPT_ID)?.dataset.key;

    if (key === injectedKey) return;

    if (injectedKey && !unloadWidget(injectedKey)) {
      if (appData?.auth?.user) window.location.reload();
      else window.location.assign("/signin");
      return;
    }

    if (key) {
      sent.current = { language, contextJson };
      injectWidget(key, { context: { ...context, "setting.language": language } });
    }
  }, [key, isPending]);

  useEffect(() => {
    if (!key) return;
    const container = document.getElementById(widgetElementId(key));
    if (!container) return;

    // The widget talks only to mercury.ai, never our backend, so it never keeps our session alive on its own
    let heartbeat: ReturnType<typeof setInterval> | undefined;

    function ping() {
      fetchEGate("GET", "authorize/login", undefined, undefined, { showNotification: false }).catch(() => undefined);
    }

    function onOpened() {
      heartbeat ??= setInterval(ping, HEARTBEAT_INTERVAL);
    }

    function onClosed() {
      clearInterval(heartbeat);
      heartbeat = undefined;
    }

    container.addEventListener("opened", onOpened);
    container.addEventListener("closed", onClosed);
    // The widget restores its open state on load, possibly before this listens
    if (container.querySelector(".chat-widget")) onOpened();

    return () => {
      container.removeEventListener("opened", onOpened);
      container.removeEventListener("closed", onClosed);
      clearInterval(heartbeat);
    };
  }, [key]);

  return null;
}
